// DAS-2532: collapsible "Urgency formula" editor — weights + levers.
// Lives at the top of the Account > Market Alerts modal, collapsed by default.
// Reads/writes /api/urgency/scoring_config; "Save & Recompute" re-scores all alerts.
import React, { useEffect, useState } from 'react';
import { Collapse, InputNumber, Button, Space, Typography, message, Spin, Tooltip } from 'antd';
import api from '../functions/api';

const { Panel } = Collapse;
const { Text } = Typography;

// Field groups rendered in the editor. label + section + key + optional hint.
const FIELDS = [
  { section: 'weights', key: 'C', label: 'Weight: Confidence', min: 0 },
  { section: 'weights', key: 'V', label: 'Weight: Validity (FP)', min: 0 },
  { section: 'weights', key: 'E', label: 'Weight: Effectiveness', min: 0 },
  { section: 'weights', key: 'O', label: 'Weight: Opportunity', min: 0 },
  { section: 'weights', key: 'R', label: 'Weight: Regime fit', min: 0 },
  { section: 'effectiveness', key: 'k', label: 'Effectiveness shrink k', min: 0, hint: 'Samples needed before a hit-rate is trusted (higher = more sceptical of small n).' },
  { section: 'effectiveness', key: 'prior', label: 'Effectiveness prior', min: 0, max: 100, hint: 'No-edge baseline an unproven alert type defaults to (50 = coin-flip).' },
  { section: 'confidence', key: 'cap', label: 'Confidence cap', min: 1, hint: 'Confidence value that maps to 100 (design scale 0–1000).' },
  { section: 'bands', key: 'high_min', label: 'Band: HIGH ≥', min: 0, max: 100 },
  { section: 'bands', key: 'moderate_min', label: 'Band: MODERATE ≥', min: 0, max: 100 },
  // DAS-2833: how hard a named rising risk hits, by how serious it is. 1 = full
  // strength, 0 = the risk is ignored. Before this every risk hit at full strength,
  // so one risk covering five sectors marked down 60% of alerts by the same amount.
  { section: 'market_fit', key: 'tier_base_case', label: 'Risk hit: base-case', min: 0, max: 1, step: 0.05,
    hint: 'How hard a risk that is the expected path counts against a matching alert. 1 = full strength.' },
  { section: 'market_fit', key: 'tier_plausible', label: 'Risk hit: plausible', min: 0, max: 1, step: 0.05,
    hint: 'Same, for a risk that could happen but is not the base case.' },
  { section: 'market_fit', key: 'tier_tail', label: 'Risk hit: long shot', min: 0, max: 1, step: 0.05,
    hint: 'Same, for a low-probability risk. Keep this small or rare risks dominate the ranking.' },
  { section: 'market_fit', key: 'tier_default', label: 'Risk hit: not yet rated', min: 0, max: 1, step: 0.05,
    hint: 'Used for a risk with no tier yet (newly added). Never full strength.' },
];

export default function UrgencyConfigSection() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/urgency/scoring_config');
      setConfig(res.data?.current || null);
    } catch (e) {
      message.error('Failed to load urgency config');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setField = (section, key, value) => {
    setConfig((c) => ({ ...c, [section]: { ...c[section], [key]: value } }));
  };

  const weightTotal = config?.weights
    ? Object.values(config.weights).reduce((s, v) => s + (Number(v) || 0), 0)
    : 0;

  const saveAndRecompute = async () => {
    if (!config) return;
    setBusy(true);
    try {
      await api.put('/urgency/scoring_config', { config });
      const res = await api.post('/urgency/recompute');
      const st = res.data?.stats || {};
      message.success(`Saved. Re-scored ${st.updated ?? '?'} alerts (bands: ${JSON.stringify(st.bands || {})}).`);
    } catch (e) {
      message.error(e?.response?.data?.error || 'Save / recompute failed');
    } finally {
      setBusy(false);
    }
  };

  const resetDefaults = async () => {
    setBusy(true);
    try {
      const res = await api.post('/urgency/scoring_config/reset');
      setConfig(res.data?.config || null);
      await api.post('/urgency/recompute');
      message.success('Reset to defaults and re-scored.');
    } catch (e) {
      message.error('Reset failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Collapse style={{ marginBottom: 16 }}>
      <Panel header="⚙ Urgency formula — weights & levers" key="urgency-config">
        {loading || !config ? (
          <Spin />
        ) : (
          <>
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              Weights are relative (they’re renormalised across whichever components an alert has).
              Current weight sum: <b>{weightTotal}</b>. Bands colour the pill (provisional).
            </Text>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              {FIELDS.map((f) => (
                <div key={`${f.section}.${f.key}`} style={{ display: 'flex', flexDirection: 'column', minWidth: 170 }}>
                  <Text style={{ fontSize: 12 }}>
                    {f.label}
                    {f.hint && (
                      <Tooltip title={f.hint}>
                        <span style={{ color: '#999', cursor: 'help' }}> ⓘ</span>
                      </Tooltip>
                    )}
                  </Text>
                  <InputNumber
                    value={config[f.section]?.[f.key]}
                    min={f.min}
                    max={f.max}
                    step={f.step ?? (f.section === 'confidence' ? 50 : 1)}
                    onChange={(v) => setField(f.section, f.key, v)}
                    style={{ width: 150 }}
                  />
                </div>
              ))}
            </div>
            <Space style={{ marginTop: 16 }}>
              <Button type="primary" loading={busy} onClick={saveAndRecompute}>
                Save &amp; Recompute
              </Button>
              <Button loading={busy} onClick={resetDefaults}>Reset to defaults</Button>
            </Space>
          </>
        )}
      </Panel>
    </Collapse>
  );
}
